import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "rc-app/components/ClientForm/CustomSelect";
import { CardBorder } from "rc-app/components/CardBorder";
import {
  getClientIdByClientAdmin,
  getClientIdByClientManger,
} from "rc-app/api/agencyActionsApi";
import UserService from "rc-app/services/UserService";
import {
  checkAgentEmailExists,
  checkAgentMobileExists,
  createAgent,
  getAgentById,
  updateAgent,
} from "rc-app/api/agentActionsApi";
import { toast } from "react-toastify";
import LogoUploadWithCrop from "rc-app/components/ImageUploadWithCrop";
import axios from "axios";

const AddAgent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("edit");
  const isEditMode = !!agentId;

  const [clientId, setClientId] = useState();
  // The logged-in ASA's own id — a new agent created by an ASA auto-assigns to
  // them (assigned_asa_id) so it shows up in their scoped Agents tab.
  const [selfAsaId, setSelfAsaId] = useState<string>("");
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(false);

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");

  // Refs for debounce timers
  const emailCheckTimeout = useRef<NodeJS.Timeout | null>(null);
  const mobileCheckTimeout = useRef<NodeJS.Timeout | null>(null);

  const validationSchema = Yup.object({
    agentType: Yup.string().required("Agent type is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(
        /^[6-9]\d{9}$/,
        "Mobile number must be 10 digits starting with 6, 7, 8, or 9",
      )
      .required("Mobile number is required"),
    company: Yup.string().when("agentType", {
      is: "external",
      then: (schema) =>
        schema.required("Company name is required for external agents"),
    }),
  });

  const formik = useFormik({
    initialValues: {
      agentType: "",
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      company: "",
      photo: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const jsonData = {
        agency_id: clientId,
        email_id: values?.email,
        first_name: values?.firstName,
        last_name: values?.lastName,
        country_code: "",
        mobile_number: values?.mobile,
        gender: "",
        date_of_birth: "",
        profile_pic: values?.photo,
        subscription_type: "basic",
        address: {
          address_line1: "",
          address_line2: "",
          landmark: "",
          postal_code: "",
          city: "",
          province: "",
          country: "",
        },
        property_updates: true,
        joined_date: new Date().toISOString().split("T")[0],
        locations_served: [
          {
            address_line1: "",
            address_line2: "",
            landmark: "",
            postal_code: "",
            city: "",
            province: "",
            country: "",
          },
        ],
        logo: "",
        agent_type: values?.agentType,
        company_name: values.agentType === "internal" ? "" : values.company,
        // An ASA owns the agents they create — auto-assign to them. ABA-created
        // agents stay unassigned (the ABA assigns them from the Agents tab).
        ...(UserService.hasRole("agency_system_admin") && selfAsaId
          ? { assigned_asa_id: selfAsaId }
          : {}),
      };

      if (isEditMode) {
        // Do NOT send joined_date on update. The backend silently drops the
        // ENTIRE write (returns 200 but persists nothing) when joined_date is
        // present in the PUT body — verified 2026-07-01 against the dev API.
        // It would also wrongly reset the agent's original join date to today.
        const { joined_date, ...editData } = jsonData;
        void joined_date;
        updateAgent(editData, agentId)
          .then(() => {
            toast.success("Agent updated successfully!");
            navigate("/agent_list");
          })
          .catch((error) => {
            const errorMessage =
              error.error_message || error.message || "Failed to update agent";
            toast.error(errorMessage);
          });
      } else {
        createAgent(jsonData)
          .then(async (res: any) => {
            // The CREATE body (CreateREAgentRequest) does NOT accept
            // assigned_asa_id — only the update body does. So when an ASA
            // creates an agent, assign it to them via a follow-up update,
            // otherwise it lands unassigned and they can't see it.
            const newId = res?.agent?._id || res?.agent?.id || res?._id;
            if (
              UserService.hasRole("agency_system_admin") &&
              selfAsaId &&
              newId
            ) {
              try {
                await updateAgent({ assigned_asa_id: selfAsaId }, newId);
              } catch (_) {
                /* agent created; can be assigned later from the ABA Agents tab */
              }
            }
            toast.success("Agent created successfully!");
            navigate("/agent_list");
          })
          .catch((error) => {
            const errorMessage =
              error.error_message || error.message || "Failed to create agent";
            toast.error(errorMessage);
          });
      }
    },
  });

  const getFieldError = (fieldName: keyof typeof formik.values): string => {
    const touched = formik.touched[fieldName];
    const error = formik.errors[fieldName];
    return touched && error ? String(error) : "";
  };

  const checkEmail = async (email: string) => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }

    // Skip check in edit mode
    if (isEditMode) {
      return;
    }

    setCheckingEmail(true);
    try {
      const existingAgent = await checkAgentEmailExists(email);
      if (existingAgent) {
        setEmailError("Agent with this email already exists");
      } else {
        setEmailError("");
      }
    } catch (error) {
      console.error("Error checking email:", error);
    } finally {
      setCheckingEmail(false);
    }
  };

  const checkMobile = async (mobile: string) => {
    if (!mobile.trim() || !/^[6-9]\d{9}$/.test(mobile)) {
      return;
    }

    // Skip check in edit mode
    if (isEditMode) {
      return;
    }

    if (!clientId) {
      return;
    }

    setCheckingMobile(true);
    try {
      const existingAgent = await checkAgentMobileExists(mobile, clientId);
      if (existingAgent) {
        setMobileError("Agent with this mobile number already exists");
      } else {
        setMobileError("");
      }
    } catch (error) {
      console.error("Error checking mobile:", error);
    } finally {
      setCheckingMobile(false);
    }
  };

  // Fetch agency ID
  useEffect(() => {
    if (UserService.hasRole("agency_business_admin")) {
      getClientIdByClientManger(UserService.getUseremail()).then((res: any) => {
        setClientId(res.agency_business_admin?.agency_id);
      });
    }
    if (UserService.hasRole("agency_system_admin")) {
      getClientIdByClientAdmin(UserService.getUseremail()).then((res: any) => {
        setClientId(res.agency_system_admin?.agency_id);
        setSelfAsaId(
          res.agency_system_admin?._id || res.agency_system_admin?.id || "",
        );
      });
    }
  }, []);

  // Load agent data if in edit mode
  useEffect(() => {
    if (isEditMode && agentId) {
      setLoading(true);
      getAgentById(agentId)
        .then((res: any) => {
          const agent = res?.agent;
          console.log("Fetched agent data:", agent);

          if (agent) {
            // Use setTimeout to ensure formik is ready
            setTimeout(() => {
              formik.setFieldValue("agentType", agent.agent_type || "");
              formik.setFieldValue("firstName", agent.first_name || "");
              formik.setFieldValue("lastName", agent.last_name || "");
              formik.setFieldValue("email", agent.email_id || "");
              formik.setFieldValue("mobile", agent.mobile_number || "");
              formik.setFieldValue("company", agent.company_name || "");
              formik.setFieldValue("photo", agent.profile_pic || "");
            }, 0);
          }
        })
        .catch((error) => {
          console.error("Error fetching agent:", error);
          toast.error("Failed to load agent data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [agentId, isEditMode]);

  useEffect(() => {
    return () => {
      if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
      if (mobileCheckTimeout.current) clearTimeout(mobileCheckTimeout.current);
    };
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedImage = e.target.files?.[0];
    if (!selectedImage) return;

    setIsUploadingLogo(true);
    const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/realestate/gc_upload`;
    const uploadFormData = new FormData();
    uploadFormData.append("gc_upload_request", selectedImage);

    try {
      const res = await axios.post(API_URL, uploadFormData, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "multipart/form-data",
        },
      });

      formik.setFieldValue("photo", res.data.uploaded_url);
    } catch (error) {
      console.error("Logo upload failed:", error);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div
        className="font-inter h-full p-3 sm:p-4 md:p-6 gap-6 flex flex-col items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #1C2C3A 0%, #1E4F52 100%)",
        }}
      >
        <div className="w-16 h-16 border-4 border-[#008564] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-white mt-4">Loading agent data...</p>
      </div>
    );
  }

  return (
    <div
      className="font-inter h-full p-3 sm:p-4 md:p-6 gap-4 sm:gap-6 flex flex-col"
      style={{
        background: "linear-gradient(180deg, #1C2C3A 0%, #1E4F52 100%)",
      }}
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm mb-4 px-1">
        <button
          onClick={() => navigate("/agent_list")}
          className="text-[#889DB2] hover:text-white transition-colors"
        >
          My Agents
        </button>
        <span className="text-[#889DB2]">/</span>
        <span className="text-white font-medium">
          {isEditMode ? "Edit Agent" : "Add New Agent"}
        </span>
      </div>

      <form onSubmit={formik.handleSubmit} className="w-full mx-auto">
        <div className="flex mb-4 sm:mb-6">
          <CardBorder
            title="Agent Type"
            borderColor="border-[#FFFFFF]"
            bgColor="bg-[#1D3440]"
          >
            <CustomSelect
              label="Agent Type"
              name="agentType"
              value={formik.values.agentType}
              type="client"
              onChange={(e: any) => {
                formik.handleChange(e);
                if (!isEditMode) {
                  formik.setTouched({});
                  formik.setErrors({});
                }
              }}
              onBlur={formik.handleBlur}
              error={
                getFieldError("agentType") ? (
                  <p className="text-red-500 text-sm mt-1">
                    {getFieldError("agentType")}
                  </p>
                ) : null
              }
              options={[
                { value: "internal", label: "Internal" },
                { value: "external", label: "External" },
              ]}
            />
          </CardBorder>
        </div>

        {formik.values.agentType && (
          <>
            <div className="flex w-full flex-col gap-4 sm:gap-6 mb-4 sm:mb-6">
              <CardBorder
                title="Agent Details"
                borderColor="border-[#FFFFFF]"
                bgColor="bg-[#1D3540]"
              >
                <div className="flex flex-col w-full rounded-lg">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex flex-col items-center sm:items-start justify-center shrink-0">
                      <p className="text-[#889DB2] text-sm sm:text-base md:text-xl font-normal mb-2">
                        Agent's Photo
                      </p>
                      <LogoUploadWithCrop
                        value={formik.values.photo}
                        onChange={(url: any) =>
                          formik.setFieldValue("photo", url)
                        }
                        onUpload={async (file) => {
                          const uploadFormData = new FormData();
                          uploadFormData.append("gc_upload_request", file);

                          try {
                            const res = await axios.post(
                              `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/realestate/gc_upload`,
                              uploadFormData,
                              {
                                headers: {
                                  Authorization: `Bearer ${UserService.getToken()}`,
                                  "ngrok-skip-browser-warning": "disabled",
                                  "Content-Type": "multipart/form-data",
                                },
                              },
                            );
                            return res.data.uploaded_url;
                          } catch (err) {
                            toast.error("Photo upload failed. Please try again.");
                            throw err;
                          }
                        }}
                        maxWidth={400}
                        maxHeight={400}
                        isUploading={isUploadingLogo}
                        builderName={`${formik.values.firstName} ${formik.values.lastName}`}
                      />

                      <input
                        type="file"
                        accept="image/*"
                        ref={logoInputRef}
                        style={{ display: "none" }}
                        onChange={handleLogoUpload}
                      />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm sm:text-base md:text-xl text-[#DCDCDC] font-normal">
                      <div>
                        <label className="block mb-1 sm:mb-2">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          placeholder="Enter..."
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                            getFieldError("firstName")
                              ? "border-red-500 bg-[#2E4051]"
                              : "border-gray-300"
                          }`}
                        />
                        {getFieldError("firstName") && (
                          <p className="text-red-500 text-sm mt-1">
                            {getFieldError("firstName")}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block mb-1 sm:mb-2">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          placeholder="Enter..."
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                            getFieldError("lastName")
                              ? "border-red-500 bg-[#2E4051]"
                              : "border-gray-300"
                          }`}
                        />
                        {getFieldError("lastName") && (
                          <p className="text-red-500 text-sm mt-1">
                            {getFieldError("lastName")}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block mb-1 sm:mb-2">Email Id</label>
                        <div className="relative">
                          <input
                            type="email"
                            name="email"
                            placeholder="Enter..."
                            value={formik.values.email}
                            onChange={(e) => {
                              formik.handleChange(e);

                              // Clear email error
                              setEmailError("");

                              // Debounced duplicate check
                              if (emailCheckTimeout.current) {
                                clearTimeout(emailCheckTimeout.current);
                              }

                              emailCheckTimeout.current = setTimeout(() => {
                                checkEmail(e.target.value);
                              }, 800);
                            }}
                            onBlur={formik.handleBlur}
                            readOnly={isEditMode}
                            className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                              getFieldError("email") || emailError
                                ? "border-red-500 bg-[#2E4051]"
                                : "border-gray-300"
                            } ${
                              isEditMode ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          />
                          {checkingEmail && !isEditMode && (
                            <div className="absolute right-3 top-3">
                              <svg
                                className="animate-spin h-5 w-5 text-[#008564]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        {(getFieldError("email") || emailError) && (
                          <p className="text-red-500 text-sm mt-1">
                            {emailError || getFieldError("email")}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-1 sm:mb-2">
                          Mobile Number
                        </label>
                        <div className="relative">
                          <input
                            type="tel"
                            name="mobile"
                            placeholder="Enter 10-digit number (6-9XXXXXXXXX)"
                            value={formik.values.mobile}
                            readOnly={isEditMode}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === "" || /^\d*$/.test(value)) {
                                if (
                                  value.length === 1 &&
                                  !["6", "7", "8", "9"].includes(value[0])
                                ) {
                                  return;
                                }

                                if (value.length <= 10) {
                                  formik.setFieldValue("mobile", value);

                                  // Clear mobile error
                                  setMobileError("");

                                  // Debounced duplicate check
                                  if (mobileCheckTimeout.current) {
                                    clearTimeout(mobileCheckTimeout.current);
                                  }

                                  if (value.length === 10) {
                                    mobileCheckTimeout.current = setTimeout(
                                      () => {
                                        checkMobile(value);
                                      },
                                      800,
                                    );
                                  }
                                }
                              }
                            }}
                            onBlur={formik.handleBlur}
                            className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                              getFieldError("mobile") || mobileError
                                ? "border-red-500 bg-[#2E4051]"
                                : "border-gray-300"
                            } ${
                              isEditMode ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          />
                          {checkingMobile && !isEditMode && (
                            <div className="absolute right-3 top-3">
                              <svg
                                className="animate-spin h-5 w-5 text-[#008564]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        {(getFieldError("mobile") || mobileError) && (
                          <p className="text-red-500 text-sm mt-1">
                            {mobileError || getFieldError("mobile")}
                          </p>
                        )}
                      </div>

                      {formik.values.agentType === "external" && (
                        <div className="sm:col-span-2">
                          <label className="block mb-1 sm:mb-2">Company Name</label>
                          <input
                            type="text"
                            name="company"
                            placeholder="Enter..."
                            value={formik.values.company}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`w-full px-3 py-2 text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors ${
                              getFieldError("company")
                                ? "border-red-500 bg-[#2E4051]"
                                : "border-gray-300"
                            }`}
                          />
                          {getFieldError("company") && (
                            <p className="text-red-500 text-sm mt-1">
                              {getFieldError("company")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardBorder>
            </div>
            <div className="flex justify-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => navigate("/agent_list")}
                className="bg-[#2E4051] text-base sm:text-xl font-medium text-white px-4 sm:px-6 py-2 rounded-lg"
                style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-[#008564] text-base sm:text-xl font-medium text-white px-4 sm:px-6 py-2 rounded-lg"
                style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
              >
                {isEditMode ? "Update" : "Save"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default AddAgent;
